import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

// The Account Number a bill-list row DISPLAYS when its collector link holds none (Philip, 2026-10-14). Display only —
// nothing is written. Order:
//   1. the collector link's account number for that year (expected_bills / pull_parcels_by_state `collector_number`)
//   2. that year's parcel_year_data.account_number
//   3. the parcel's own account_number
// Used by Tax Bill Processing and Approvals. Unit-tested: src/__tests__/account-fallback.test.js.
//
// ⛔ The catch-up years (DTAG 2021–2025) were linked to their collectors with no account number, so every row read "—"
// even though the parcel's year row and the parcel itself carry one.

const blank = (v) => v == null || String(v).trim() === '';

// row: { parcel_id, collector_number }; fb: { years: Map(parcel_id → acct), parcels: Map(parcel_id → acct) }
// → { value, source } — source is 'collector link' | 'parcel year record' | 'parcel record' | null.
export function resolveAccount(row, fb) {
  if (!blank(row?.collector_number)) return { value: String(row.collector_number).trim(), source: 'collector link' };
  const y = fb?.years?.get(row?.parcel_id);
  if (!blank(y)) return { value: String(y).trim(), source: 'parcel year record' };
  const p = fb?.parcels?.get(row?.parcel_id);
  if (!blank(p)) return { value: String(p).trim(), source: 'parcel record' };
  return { value: null, source: null };
}

export const accountTitle = (r, year) => (r.source
  ? `Account number from the ${r.source}${r.source === 'parcel record' ? '' : ` (${year})`}`
  : 'No account number on the collector link, the parcel year record or the parcel record');

const CHUNK = 200;   // ids per .in() — keeps the request URL short

// Loads the fallbacks for the rows whose collector link has no account number. → { years, parcels } (Maps).
export async function loadAccountFallback(rows, year) {
  const ids = [...new Set((rows || []).filter(r => r.parcel_id && blank(r.collector_number)).map(r => r.parcel_id))];
  const years = new Map(), parcels = new Map();
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const [yr, pr] = await Promise.all([
      supabase.from('parcel_year_data').select('parcel_id, account_number').eq('tax_year', String(year)).in('parcel_id', chunk),
      supabase.from('parcels').select('id, account_number').in('id', chunk),
    ]);
    if (yr.error) console.warn('[accountFallback] parcel_year_data:', yr.error.message);
    if (pr.error) console.warn('[accountFallback] parcels:', pr.error.message);
    (yr.data || []).forEach(r => years.set(r.parcel_id, r.account_number));
    (pr.data || []).forEach(r => parcels.set(r.id, r.account_number));
  }
  return { years, parcels };
}

// React: the fallback Maps for a list's rows, reloaded when the rows or year change. Empty until loaded (the row
// shows its collector link's number, or "—", meanwhile).
export function useAccountFallback(rows, year) {
  const [fb, setFb] = useState(() => ({ years: new Map(), parcels: new Map() }));
  useEffect(() => {
    let alive = true;
    loadAccountFallback(rows, year).then(r => { if (alive) setFb(r); });
    return () => { alive = false; };
  }, [rows, year]);
  return fb;
}
